/**
 * The settings screen's stylesheet, injected with the screen and removed with it.
 *
 * Same palette and same measure as the main menu, because this screen opens over it and a second
 * typeface or a second width would read as a different application. It sits above the menu and
 * above a running game's HUD, under the session notices, which are the one thing that has to be
 * readable whatever is up.
 *
 * Every target is at least 44 pixels, the slider included: the thumb on a native range input is
 * smaller than a finger, so the track carries the height and the thumb is grown to match.
 *
 * Every state on this screen is said at least twice: a colour and a position, or a colour and a
 * weight. It is the screen a player opens because one channel is not reaching them, and a control
 * whose state is a hue alone would be the section failing at its own subject.
 */

import { injectCss } from '@/ui/cssInjector';

const CSS = `
.settings {
  position: fixed;
  inset: 0;
  z-index: 25;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding:
    calc(env(safe-area-inset-top, 0px) + clamp(20px, 5vh, 56px))
    calc(env(safe-area-inset-right, 0px) + clamp(16px, 5vw, 64px))
    calc(env(safe-area-inset-bottom, 0px) + clamp(20px, 5vh, 48px))
    calc(env(safe-area-inset-left, 0px) + clamp(16px, 5vw, 64px));
  background: rgba(8, 9, 11, 0.9);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
  font: 500 16px/1.5 ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  color: #f2ede3;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.settings-page {
  display: flex;
  flex-direction: column;
  gap: clamp(18px, 3vh, 30px);
  width: 100%;
  max-width: 860px;
  margin-inline: auto;
}

.settings-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.settings-title {
  margin: 0;
  font-size: clamp(15px, 4vw, 20px);
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
}

.settings-close {
  min-height: 44px;
  padding: 0 18px;
  border: 1px solid rgba(242, 237, 227, 0.28);
  border-radius: 6px;
  background: rgba(242, 237, 227, 0.06);
  color: inherit;
  font: inherit;
  font-size: clamp(11px, 3vw, 12.5px);
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  cursor: pointer;
}

.settings-close:hover,
.settings-close:focus-visible {
  border-color: rgba(232, 179, 74, 0.7);
  color: #e8b34a;
}

.settings-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.settings-section {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: rgba(232, 179, 74, 0.85);
}

.settings-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 20px;
  padding: 14px 16px;
  border: 1px solid rgba(242, 237, 227, 0.14);
  border-radius: 8px;
  background: rgba(22, 24, 28, 0.7);
}

.settings-words {
  flex: 1 1 260px;
  min-width: 0;
}

.settings-label {
  font-size: 14px;
}

.settings-hint {
  margin: 4px 0 0;
  color: rgba(242, 237, 227, 0.55);
  font-size: 12px;
  line-height: 1.4;
}

.settings-control {
  flex: 1 1 260px;
  display: flex;
  align-items: center;
  gap: 14px;
}

.settings-slider {
  flex: 1 1 auto;
  min-width: 140px;
  min-height: 44px;
  accent-color: #e8b34a;
  cursor: pointer;
}

.settings-readout {
  min-width: 5ch;
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: #e8b34a;
}

/*
 * The button that plays what the slider sets. Sized like every other target on this screen, and
 * quiet: it sits beside a control rather than competing with it, because it is a thing you press
 * once to check rather than a thing the row is about.
 */
.settings-audition {
  flex: 0 0 auto;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid rgba(242, 237, 227, 0.28);
  border-radius: 6px;
  background: rgba(242, 237, 227, 0.06);
  color: inherit;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}

.settings-audition:hover,
.settings-audition:focus-visible {
  border-color: rgba(232, 179, 74, 0.7);
}

/*
 * A row of options rather than a track. Wrapping, because two labels plus a long knob label is more
 * than a phone has across, and a button that has been pushed off the edge is a choice nobody can
 * make. Every button carries the 44 pixel target the close button does.
 */
.settings-choice {
  flex-wrap: wrap;
  gap: 10px;
}

.settings-choiceOption {
  flex: 1 1 auto;
  min-height: 44px;
  padding: 0 16px;
  border: 1px solid rgba(242, 237, 227, 0.28);
  border-radius: 6px;
  background: rgba(242, 237, 227, 0.06);
  color: inherit;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}

.settings-choiceOption:hover,
.settings-choiceOption:focus-visible {
  border-color: rgba(232, 179, 74, 0.7);
}

/*
 * Which one is on, said three ways: the border, the fill and the weight. A single accent colour
 * would be the one channel a player may not have, on the screen where they went to fix exactly
 * that.
 */
.settings-choiceOption.is-chosen {
  border-color: #e8b34a;
  background: rgba(232, 179, 74, 0.18);
  color: #e8b34a;
  font-weight: 700;
}

/*
 * A switch: a track with a knob at one end, and the word beside it. Both channels, for the reason
 * the chosen option above states twice over, because this is the control the accessibility section
 * is made of and a switch a player cannot read is the joke writing itself.
 *
 * The whole target is the track, which carries the 44 pixels every other target on this screen
 * does. The word is outside it so the button is a switch rather than a button with a caption in it.
 */
.settings-toggle {
  gap: 12px;
}

.settings-toggleSwitch {
  flex: 0 0 auto;
  width: 62px;
  min-height: 44px;
  padding: 0;
  border: 1px solid rgba(242, 237, 227, 0.28);
  border-radius: 22px;
  background: rgba(242, 237, 227, 0.06);
  cursor: pointer;
}

.settings-toggleSwitch::before {
  content: "";
  display: block;
  width: 18px;
  height: 18px;
  margin-inline: 6px;
  border-radius: 50%;
  background: rgba(242, 237, 227, 0.55);
  transition: margin 120ms ease, background 120ms ease;
}

.settings-toggleSwitch:hover,
.settings-toggleSwitch:focus-visible {
  border-color: rgba(232, 179, 74, 0.7);
}

/* Position, fill and border at once, so the state survives any one of them being invisible. */
.settings-toggleSwitch.is-on {
  border-color: #e8b34a;
  background: rgba(232, 179, 74, 0.18);
}

.settings-toggleSwitch.is-on::before {
  margin-inline: 34px 6px;
  background: #e8b34a;
}

.settings-toggleState {
  min-width: 4ch;
  font-size: 13px;
  color: rgba(242, 237, 227, 0.75);
}

/*
 * The binding rows: verb, key cap, reset, stacked down the panel. The key cap is the target and
 * carries the 44 pixels every other target here does; capturing is said with the accent border and
 * the words on the cap, two channels like every other state on this screen.
 */
.settings-bindings {
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}

.settings-bindingRow {
  display: flex;
  align-items: center;
  gap: 10px;
}

.settings-bindingAction {
  flex: 1 1 auto;
  font-size: 13px;
}

.settings-bindingKeys,
.settings-bindingReset,
.settings-bindingAnswer,
.settings-bindingResetAll {
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid rgba(242, 237, 227, 0.28);
  border-radius: 6px;
  background: rgba(242, 237, 227, 0.06);
  color: inherit;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}

.settings-bindingKeys {
  flex: 0 0 auto;
  min-width: 12ch;
}

.settings-bindingKeys:hover,
.settings-bindingKeys:focus-visible,
.settings-bindingReset:hover,
.settings-bindingReset:focus-visible,
.settings-bindingAnswer:hover,
.settings-bindingAnswer:focus-visible,
.settings-bindingResetAll:hover,
.settings-bindingResetAll:focus-visible {
  border-color: rgba(232, 179, 74, 0.7);
}

.settings-bindingKeys.is-capturing {
  border-color: #e8b34a;
  background: rgba(232, 179, 74, 0.18);
  color: #e8b34a;
  font-weight: 700;
}

.settings-bindingAnswers {
  display: flex;
  gap: 10px;
}

.settings-bindingAnswers.is-hidden {
  display: none;
}

.settings-bindingNote {
  margin: 0;
  min-height: 1.2em;
  font-size: 13px;
  color: rgba(242, 237, 227, 0.75);
}

@media (prefers-reduced-motion: reduce) {
  .settings {
    -webkit-backdrop-filter: none;
    backdrop-filter: none;
  }

  /* The knob still moves. It arrives where it arrives, without sliding there. */
  .settings-toggleSwitch::before {
    transition: none;
  }
}
`;

export function injectSettingsStyles(): () => void {
  return injectCss(CSS);
}
